import React, { useState } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
  Icons,
} from '../../../components';

import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

const getListingVideoUrl = listing => {
  const v = listing?.attributes?.publicData?.video;
  if (!v) return null;
  if (Array.isArray(v) && v.length > 0) {
    return v[0]?.secureUrl || v[0]?.url || null;
  }
  return v.secureUrl || v.url || null;
};

// Helper: try to derive a JPG thumbnail from a Cloudinary video secureUrl.
// Falls back to null if not a Cloudinary URL or parsing fails.
const makeCloudinaryVideoThumb = secureUrl => {
  try {
    if (!secureUrl) return null;
    const u = new URL(secureUrl);
    // Replace '/video/upload/' with '/video/upload/so_1/' (seek 1s) and swap extension to .jpg
    let newPath = u.pathname.replace('/video/upload/', '/video/upload/so_1/');
    newPath = newPath.replace(/\.(mp4|mov|webm|mkv|avi)$/i, '.jpg');
    u.pathname = newPath;
    return u.toString();
  } catch {
    return null;
  }
};

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @param {Object} [props.listing] - Full listing object (used to read publicData.video)
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const intl = useIntl();
  const { rootClassName, className, images, imageVariants, thumbnailVariants, listing } = props;
  const thumbVariants = thumbnailVariants || imageVariants;

  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);

  // VIDEO: read from publicData.video (if any)
  const videoUrl = getListingVideoUrl(listing);
  const videoThumbUrl = makeCloudinaryVideoThumb(videoUrl);

  // Build gallery items: optional video first, then images
  const videoItems = videoUrl
    ? [
        {
          type: 'video',
          url: videoUrl,
          alt: intl.formatMessage({
            id: 'ListingImageGallery.videoAltText',
            defaultMessage: 'Listing video',
          }),
          thumbAlt: intl.formatMessage({
            id: 'ListingImageGallery.videoThumbAltText',
            defaultMessage: 'Video',
          }),
          thumbnail: videoThumbUrl || null,
        },
      ]
    : [];

  const imageItems = images.map((img, i) => {
    return {
      type: 'image',
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
    };
  });

  const items = [...videoItems, ...imageItems];

  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };

  const renderItem = item => {
    // Render video slide
    if (item.type === 'video') {
      const stop = e => {
        // let the browser handle the video control, but don't let the gallery see it
        e.stopPropagation();
      };
      return (
        <AspectRatioWrapper
          width={16}
          height={9}
          className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
        >
          <div className={css.itemCentering}>
            <video src={item.url} controls playsInline className={css.videoPlayer} />
          </div>
        </AspectRatioWrapper>
      );
    }

    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
      >
        <div className={css.itemCentering}>
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            {...imageSizesMaybe}
          />
        </div>
      </AspectRatioWrapper>
    );
  };

  const renderThumbInner = item => {
    // Render video thumbnail (prefer derived JPG; fallback to a simple badge)
    if (item.type === 'video') {
      if (item.thumbnail) {
        return (
          <div className={css.videoThumbWrapper} title={item.thumbAlt} aria-label={item.thumbAlt}>
            <img
              src={item.thumbnail}
              alt={item.thumbAlt || 'Video thumbnail'}
              className={css.videoThumbImage}
            />
            <div className={css.videoBadge}>▶</div>
          </div>
        );
      }
      return (
        <div className={css.videoThumb} aria-label={item.thumbAlt} title={item.thumbAlt}>
          <span>🎥</span>
        </div>
      );
    }

    return (
      <div className={css.videoThumbWrapper}>
        <ResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="88px"
        />
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <Icons name="leftAngle" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <Icons name="rightAngle" />
        </div>
      </button>
    );
  };
  const renderFullscreenButton = (onClick, isFullscreen) => {
    return isFullscreen ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : (
      <button className={css.openFullscreen} onClick={onClick}>
        <FormattedMessage
          id="ListingImageGallery.viewImagesButton"
          values={{ count: images.length }}
        />
      </button>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <ReactImageGallery
      additionalClass={classes}
      items={items}
      renderItem={renderItem}
      renderThumbInner={renderThumbInner}
      onScreenChange={onScreenChange}
      renderLeftNav={renderLeftNav}
      renderRightNav={renderRightNav}
      renderFullscreenButton={renderFullscreenButton}
      {...IMAGE_GALLERY_OPTIONS}
    />
  );
};

export default ListingImageGallery;
